import { useMemo, useState } from "react";
import { FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import * as Icons from "@/assets/features/candidates";
import BaseModal from "@/components/shared/modals/BaseModal";
import { useTranslation } from "react-i18next";
import { toastError } from "@/lib/Toast";
import { useUser } from "@/stores/authStore";
import { exportTable, exportableColumns } from "@/utils/tableExport";

export default function ExportModal({ open, onOpenChange, table, exportConfig }) {
  const { t } = useTranslation();
  const user = useUser();
  const isTableExport = Boolean(table && exportConfig);

  const [originalFormat, setOriginalFormat] = useState(true);
  const [originalTemplate, setOriginalTemplate] = useState(true);
  const [format, setFormat] = useState("pdf");
  const [allPages, setAllPages] = useState(false);
  const [loading, setLoading] = useState(false);

  const canExportAll = typeof exportConfig?.getAllRows === "function";
  const isEmpty = isTableExport && table.getRowModel().rows.length === 0;

  const exportOptions = useMemo(
    () =>
      isTableExport
        ? [
            { id: "pdf", label: t("common.exportModal.optionPdf"), icon: Icons.PdfDocument },
            { id: "xlsx", label: t("common.exportModal.optionExcel"), icon: Icons.Excal },
            { id: "csv", label: t("common.exportModal.optionCsv"), icon: FileText },
          ]
        : [
            { id: "pdf", label: t("common.exportModal.optionPdf"), icon: Icons.PdfDocument },
            { id: "excel", label: t("common.exportModal.optionExcel"), icon: Icons.Excal },
            { id: "xml", label: t("common.exportModal.optionXml"), icon: Icons.Xml },
            { id: "template", label: t("common.exportModal.optionTemplate"), icon: Icons.CV },
          ],
    [t, isTableExport],
  );

  const handleExport = async () => {
    if (!isTableExport) {
      onOpenChange(false);
      return;
    }
    setLoading(true);
    try {
      const sourceRows =
        allPages && canExportAll
          ? (await exportConfig.getAllRows()) ?? table.getRowModel().rows
          : table.getRowModel().rows;
      await exportTable({
        format,
        title: exportConfig.title ?? t("common.exportModal.title"),
        columns: exportableColumns(table),
        sourceRows,
        companyName: user?.companyName,
      });
      onOpenChange(false);
    } catch (error) {
      toastError({ header: t("common.exportModal.failed"), description: error?.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      open={open}
      onClose={() => onOpenChange(false)}
      onConfirm={handleExport}
      title={t("common.exportModal.title")}
      textBtn={t("common.exportModal.exportBtn")}
      loading={loading}
    >
      <div className="space-y-3 text-md text-secondary pb-4">
        <p className="text-sm text-black">{t("common.exportModal.shareAs")}</p>

        {exportOptions.map(({ id, label, icon: Icon }) => {
          const selected = isTableExport && format === id;
          return (
            <div
              key={id}
              role="button"
              tabIndex={0}
              onClick={() => isTableExport && setFormat(id)}
              onKeyDown={(e) => e.key === "Enter" && isTableExport && setFormat(id)}
              className={`border rounded-[1rem] p-3 flex items-center gap-3 cursor-pointer ${
                selected
                  ? "border-primary bg-[#D8EBF7]/60 text-primary"
                  : "border-[#B9CCE2] hover:bg-gray-50"
              }`}
            >
              <Icon className="w-6 h-6" />
              <span>{label}</span>
            </div>
          );
        })}

        <div className="flex items-center gap-4 pt-2">
          {isTableExport ? (
            <>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={!allPages} onCheckedChange={() => setAllPages(false)} />
                {t("common.exportModal.firstPage")}
              </label>
              <label
                className={`flex items-center gap-2 text-sm ${
                  canExportAll ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                }`}
              >
                <Checkbox
                  checked={allPages}
                  disabled={!canExportAll}
                  onCheckedChange={() => setAllPages(true)}
                />
                {t("common.exportModal.allPages")}
              </label>
            </>
          ) : (
            <>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={originalFormat} onCheckedChange={(val) => setOriginalFormat(!!val)} />
                {t("common.exportModal.originalFormat")}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={originalTemplate} onCheckedChange={(val) => setOriginalTemplate(!!val)} />
                {t("common.exportModal.originalTemplate")}
              </label>
            </>
          )}
        </div>

        {isEmpty && <p className="text-sm">{t("common.exportModal.empty")}</p>}
      </div>
    </BaseModal>
  );
}
